import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { queryKeys } from "@/lib/queryKeys";
import { isSuperAdmin } from "@/types";
import type { AuthUser, CandidateNote } from "@/types";

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return "";
  const now = Date.now();
  const then = new Date(dateStr.endsWith("Z") ? dateStr : dateStr + "Z").getTime();
  const seconds = Math.floor((now - then) / 1000);
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days === 1) return "Yesterday";
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

export function CandidateNotes({
  candidateId,
  currentUser,
}: {
  candidateId: number;
  currentUser: AuthUser;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingDraft, setEditingDraft] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: queryKeys.notes(candidateId),
    queryFn: () => api.listNotes(candidateId),
  });
  const notes: CandidateNote[] = data?.notes ?? [];

  const refresh = () => queryClient.invalidateQueries({ queryKey: queryKeys.notes(candidateId) });

  const create = useMutation({
    mutationFn: (content: string) => api.createNote({ candidate_id: candidateId, content }),
    onSuccess: () => { setDraft(""); refresh(); },
  });

  const update = useMutation({
    mutationFn: ({ id, content }: { id: number; content: string }) => api.updateNote(id, content),
    onSuccess: () => { setEditingId(null); setEditingDraft(""); refresh(); },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.deleteNote(id),
    onSuccess: refresh,
  });

  const isOwn = (note: CandidateNote) =>
    note.created_by === currentUser.id ||
    isSuperAdmin(currentUser) ||
    currentUser.role === "admin";

  return (
    <div className="space-y-4">
      <div>
        <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
          Add note
        </label>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          rows={3}
          placeholder="Phone screen feedback, references checked, follow-up reminders…"
          className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2 text-sm text-near-black focus:outline-none focus:shadow-focus"
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={() => create.mutate(draft.trim())}
            disabled={!draft.trim() || create.isPending}
            className="rounded-btn bg-near-black px-4 py-1.5 text-xs font-medium text-white transition-colors hover:bg-rausch disabled:opacity-50"
          >
            {create.isPending ? "Posting…" : "Post note"}
          </button>
        </div>
      </div>

      <div className="space-y-2 border-t border-surface-secondary pt-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-near-black">History ({notes.length})</h3>
        </div>

        {isLoading ? (
          <p className="text-xs text-text-secondary">Loading…</p>
        ) : notes.length === 0 ? (
          <p className="text-xs italic text-text-secondary">No notes yet.</p>
        ) : (
          notes.map((n) => {
            const author = n.author_display_name || n.author_name || "Unknown";
            const isEditing = editingId === n.id;
            return (
              <div key={n.id} className="rounded-card bg-white p-3 shadow-card">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <div className="flex h-7 w-7 items-center justify-center rounded-full bg-near-black text-[10px] font-bold text-white">
                      {author[0]?.toUpperCase() ?? "?"}
                    </div>
                    <div>
                      <p className="text-xs font-semibold text-near-black">{author}</p>
                      <p className="text-[10px] text-text-secondary">
                        {timeAgo(n.created_at)}
                        {n.updated_at && " (edited)"}
                      </p>
                    </div>
                  </div>
                  {isOwn(n) && !isEditing && (
                    <div className="flex gap-1">
                      <button
                        onClick={() => { setEditingId(n.id); setEditingDraft(n.content); }}
                        className="rounded-btn px-2 py-0.5 text-[10px] font-medium text-text-secondary transition-colors hover:bg-surface-secondary hover:text-near-black"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => { if (confirm("Delete this note?")) remove.mutate(n.id); }}
                        className="rounded-btn px-2 py-0.5 text-[10px] font-medium text-text-secondary transition-colors hover:bg-rose-100 hover:text-error-text"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
                {isEditing ? (
                  <div className="mt-2 space-y-2">
                    <textarea
                      value={editingDraft}
                      onChange={(e) => setEditingDraft(e.target.value)}
                      rows={3}
                      className="w-full rounded-btn border border-border-card bg-white px-2 py-1.5 text-sm text-near-black focus:outline-none focus:shadow-focus"
                    />
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => { setEditingId(null); setEditingDraft(""); }}
                        className="rounded-btn px-3 py-1 text-xs font-medium text-text-secondary hover:text-near-black"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={() => update.mutate({ id: n.id, content: editingDraft.trim() })}
                        disabled={!editingDraft.trim() || update.isPending}
                        className="rounded-btn bg-near-black px-3 py-1 text-xs font-medium text-white hover:bg-rausch disabled:opacity-50"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="mt-2 whitespace-pre-wrap text-sm text-text-focused">{n.content}</p>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
